/**
 * Font-related @apply property sets from src/styles/propertySets.css, as sx objects.
 * Mirrors the source verbatim. Custom-media → literal sx keys:
 * --viewportSmall '@media (min-width:600px)', --viewportMedium '900px', --viewportLarge '1200px',
 * --viewportLargeWithPaddings '1304px', --viewportXLarge '1921px'.
 * @param {Record<string,string>} tokens - createTokens() result
 * @returns {Record<string, object>}
 */
export const createFontStyleMixins = (tokens) => ({
  marketplaceDefaultFontStyles: {
    fontFamily: tokens.fontFamily,
    fontWeight: tokens.fontWeightMedium,
    fontSize: '14px',
    lineHeight: '24px',
    letterSpacing: '-0.1px',
    '@media (min-width:900px)': {
      fontSize: '16px',
      lineHeight: '32px',
    },
  },

  marketplaceBodyFontStyles: {
    fontFamily: tokens.fontFamily,
    fontWeight: tokens.fontWeightMedium,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '-0.1px',
    marginBottom: '12px',
    '@media (min-width:900px)': {
      fontSize: '20px',
      lineHeight: '32px',
      marginBottom: '16px',
    },
  },

  marketplaceSmallFontStyles: {
    fontFamily: tokens.fontFamily,
    fontWeight: tokens.fontWeightMedium,
    fontSize: '14px',
    lineHeight: '24px',
  },

  marketplaceTinyFontStyles: {
    fontFamily: tokens.fontFamily,
    fontWeight: tokens.fontWeightRegular,
    fontSize: '13px',
    lineHeight: '18px',
    marginTop: '9.5px',
    marginBottom: '8.5px',
    WebkitFontSmoothing: 'subpixel-antialiased',
    '@media (min-width:900px)': {
      lineHeight: '16px',
      marginTop: '10.5px',
      marginBottom: '13.5px',
    },
  },

  marketplaceH1FontStyles: {
    fontWeight: tokens.fontWeightSemiBold,
    fontSize: '24px',
    lineHeight: '30px',
    letterSpacing: '-0.5px',
    marginTop: '18px',
    marginBottom: '18px',
    '@media (min-width:900px)': {
      fontSize: '40px',
      fontWeight: tokens.fontWeightSemiBold,
      lineHeight: '48px',
      letterSpacing: '-1px',
      marginTop: '24px',
      marginBottom: '24px',
    },
  },

  marketplaceH2FontStyles: {
    fontWeight: tokens.fontWeightSemiBold,
    fontSize: '21px',
    lineHeight: '24px',
    marginTop: '21px',
    marginBottom: '17px',
    '@media (min-width:900px)': {
      lineHeight: '32px',
      fontSize: '32px',
      marginTop: '21px',
      marginBottom: '19px',
    },
  },

  marketplaceH3FontStyles: {
    fontWeight: tokens.fontWeightSemiBold,
    fontSize: '18px',
    lineHeight: '24px',
    marginTop: '16px',
    marginBottom: '14px',
    '@media (min-width:900px)': {
      fontSize: '24px',
      marginTop: '16px',
      marginBottom: '16px',
    },
  },

  marketplaceH4FontStyles: {
    fontWeight: tokens.fontWeightMedium,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0',
    marginTop: '12px',
    marginBottom: '12px',
    '@media (min-width:900px)': {
      fontWeight: tokens.fontWeightMedium,
      fontSize: '20px',
      marginTop: '17px',
      marginBottom: '15px',
    },
  },

  marketplaceH5FontStyles: {
    fontWeight: tokens.fontWeightMedium,
    fontSize: '14px',
    lineHeight: '18px',
    letterSpacing: '0',
    marginTop: '10px',
    marginBottom: '8px',
    '@media (min-width:900px)': {
      lineHeight: '16px',
      marginTop: '10px',
      marginBottom: '14px',
    },
  },

  marketplaceH6FontStyles: {
    fontWeight: tokens.fontWeightBold,
    fontSize: '12px',
    lineHeight: '18px',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    marginTop: '10px',
    marginBottom: '8px',
    '@media (min-width:900px)': {
      lineHeight: '16px',
      marginTop: '10px',
      marginBottom: '6px',
    },
  },

  marketplaceHeroTitleFontStyles: {
    fontWeight: tokens.fontWeightBold,
    fontSize: '36px',
    lineHeight: '36px',
    letterSpacing: '-0.7px',
    marginTop: '25px',
    marginBottom: '14px',
    '@media (min-width:900px)': {
      fontSize: '64px',
      lineHeight: '78px',
      letterSpacing: '-1.5px',
      marginTop: '25px',
      marginBottom: '23px',
    },
  },

  marketplaceListingAttributeFontStyles: {
    fontFamily: tokens.fontFamily,
    fontWeight: tokens.fontWeightMedium,
    fontSize: '16px',
    lineHeight: '24px',
  },

  marketplaceSearchFilterLabelFontStyles: {
    fontFamily: tokens.fontFamily,
    fontWeight: tokens.fontWeightSemiBold,
    fontSize: '13px',
    lineHeight: '18px',
    '@media (min-width:900px)': {
      fontWeight: tokens.fontWeightMedium,
      fontSize: '16px',
      lineHeight: '20px',
    },
  },

  marketplaceSearchFilterSublabelFontStyles: {
    fontFamily: tokens.fontFamily,
    fontWeight: tokens.fontWeightMedium,
    fontSize: '18px',
    lineHeight: '18px',
  },

  marketplaceMessageFontStyles: {
    fontFamily: tokens.fontFamily,
    fontWeight: tokens.fontWeightRegular,
    fontSize: '16px',
    lineHeight: '24px',
  },

  marketplaceMessageDateFontStyles: {
    fontFamily: tokens.fontFamily,
    fontWeight: tokens.fontWeightRegular,
    fontSize: '12px',
    lineHeight: '18px',
    '@media (min-width:900px)': {
      fontWeight: tokens.fontWeightMedium,
      fontSize: '12px',
      lineHeight: '24px',
    },
  },

  marketplaceTabNavFontStyles: {
    fontWeight: tokens.fontWeightMedium,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0',
    '@media (min-width:1200px)': {
      fontWeight: tokens.fontWeightSemiBold,
      fontSize: '20px',
      lineHeight: '24px',
    },
  },

  marketplaceTabNavHorizontalFontStyles: {
    fontWeight: tokens.fontWeightMedium,
    fontSize: '14px',
    lineHeight: '24px',
    letterSpacing: '0',
  },

  marketplaceTxTransitionFontStyles: {
    fontFamily: tokens.fontFamily,
    fontWeight: tokens.fontWeightMedium,
    fontSize: '16px',
    lineHeight: '18px',
    '@media (min-width:900px)': {
      fontSize: '16px',
      lineHeight: '24px',
    },
  },
});
